<!-- NarrativeTabs.vue -->
<template>
  <div class="narrative-tabs">
    <!-- Tab headers -->

    <!-- Tab content -->
    <div class="tab-content">
      <div
        v-for="(section, index) in sections"
        :key="index"
        v-show="activeTab === index"
        class="tab-pane"
      >
        <conversation-player
          :is-pro="isPro"
          :document-data="documentData"
          :replay-conversation="replayConversation"
          :last-document-update="lastDocumentUpdate"
          :all-message-ids="allMessageIds"
          :latest-user-input="latestUserInput"
          :intro-finished="introFinished"
          :search-results="searchResults"
          :is-latest-message="isLatestMessage"
          :raw-response="section"
          :shareId="chatId"
          :is-shared-view="isSharedView"
          :messageId="messageId"
          :autoPlay="true"
          :isResponseInProgress="isResponseInProgress"
          :voice-settings="voiceSettings"
          @expanded-view-toggled="$emit('expanded-view-toggled', $event)"
          @podcast-playing="(isPlaying, msgId) => $emit('podcast-playing', isPlaying, msgId)"
          @segment-end="(activeSegment, msgId) => $emit('segment-end', activeSegment, msgId)"
          @segment-start="(activeSegment, msgId) => $emit('segment-start', activeSegment, msgId)"
          @conversation-end="$emit('conversation-end', $event)"
          @download-start="$emit('download-start', $event)"
          @download-end="$emit('download-end', $event)"
          @download-triggered="$emit('download-triggered', $event)"
          @parse-error="handleError"
          @segment-citations="$emit('segment-citations', $event)"
          @citation-click="$emit('citation-click', $event)"
          @citation-speaking="$emit('citation-speaking', $event)"
          @auto-scroll-complete="$emit('auto-scroll-complete')"
          @full-audio-ready="$emit('full-audio-ready')"
          @audio-progress="$emit('audio-progress', $event)"
          @auto-scroll-progress="$emit('auto-scroll-progress', $event)"
        />
      </div>
    </div>

    <!-- Error display -->
    <div v-if="error" class="error-message">
      {{ error }}
    </div>
  </div>
</template>

<script>
import ConversationPlayer from './ConversationPlayer.vue';

export default {
  name: 'NarrativeTabs',
  components: {
    ConversationPlayer
  },
  props: {
    replayConversation: {
      type: Boolean,
      default: false
    },
    isPro: {
      type: Boolean,
      default: false
    },
    searchResults: {
      type: Array,
      default: () => []
    },
    documentData: {
      type: Object,
      default: () => ({})
    },
    lastDocumentUpdate: {
      type: Number,
      default: 0
    },
    allMessageIds: {
      type: Array,
      default: () => []
    },
    autoPlay: {
      type: Boolean,
      default: false
    },
    latestUserInput: {
      type: String,
      default: null
    },
    isLatestMessage: {
      type: Boolean,
      default: false
    },
    isResponseInProgress: {
      type: Boolean,
      default: false
    },
    introFinished: {
      type: Boolean,
      default: false
    },
    messageId: {
      required: true
    },
    userInput: {
      type: String,
      default: null
    },
    text: {
      type: String,
      required: true
    },
    chatId: {
      type: String,
      default: null
    },
    isSharedView: {
      type: Boolean,
      default: false
    },
    voiceSettings: {
      type: Object,
      default: () => ({})
    }
  },
  emits: ['citation-click', 'tab-changed'],
  data() {
    return {
      activeTab: 0,
      error: null
    }
  },
  computed: {
    sections() {
      if (!this.text) return [];

      const matches = this.text.match(/NARRATIVE ANSWER:([^]*?)(?=\n*NARRATIVE ANSWER:|\n*$)/g);
      if (!matches) {
        // If no NARRATIVE ANSWER sections found, return original text as single section
        return [this.text.trim()];
      }

      // Process multiple narrative sections
      return matches
        .map(section => section.replace(/NARRATIVE ANSWER:\s*/m, '').trim())
        .filter(section => section.length > 0);
    }
  },
  methods: {
    trimUserInput(input) {
      if (!input) return null;
      return input.length > 30
        ? input.slice(0, 30) + '...'
        : input;
    },
    formatTime(seconds) {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    },
    onTabClick(index) {
      this.activeTab = index;
      this.error = null;
      this.$emit('tab-changed', index);
    },
    handleError(error) {
      this.error = error;
    }
  }
}
</script>

<style scoped>
.narrative-tabs {
  width: 100%;
  padding: 0;
  margin: 0 auto;
}

@media screen and (max-width: 768px) {
  .narrative-tabs {
    padding: 0px;
  }
}

.tab-headers {
  display: flex;
  padding: 0 16%;
}

@media screen and (max-width: 768px) {
  .tab-headers {
    padding: 5px;
  }
}

.tab-button {
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: #718096;
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  cursor: pointer;
  transition: all 0.2s;
}

@media screen and (max-width: 768px) {
  .tab-button {
    margin-left: 10px;
  }
}

.tab-button:hover {
  color: #2d3748;
}

.tab-button.active {
  color: #4299e1;
  border-bottom-color: #4299e1;
}

.tab-content {
  padding: 0;
}

@media screen and (max-width: 768px) {
  .tab-content {
    padding: 0 0.5rem;
  }
}

.tab-pane {
  width: 100%;
}

.error-message {
  margin-top: 1rem;
  padding: 0.75rem;
  background-color: #FEE2E2;
  color: #DC2626;
  border-radius: 0.375rem;
  font-size: 0.875rem;
}
.segment {
  padding: 0.5rem;
  margin-bottom: 1rem;
  border-radius: 0.5rem;
  background: #2d3748;
  border-left: 4px solid transparent;
  transition: all 0.3s ease;
  position: relative;
}

.segment-host {
  background: #2c5282;
  border-left-color: #4299e1;
}

.segment-expert {
  background: #2d3748;
  border-left-color: #48bb78;
}

.segment.active {
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.speaker-info {
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.speaker-name {
  font-weight: 600;
  color: #4299e1;
}

.speaker-role {
  color: #a0aec0;
  font-size: 0.875rem;
}

.content {
  color: #e2e8f0;
  line-height: 1.6;
  margin: 0;
}

.timestamp {
  position: absolute;
  top: 1rem;
  right: 1rem;
  color: #718096;
  font-size: 0.875rem;
}

.fade-enter-active, .fade-leave-active {
  transition: all 0.3s ease;
}

.fade-enter-from, .fade-leave-to {
  opacity: 0;
  transform: translateY(10px);
}

.loading-state, .error-state {
  padding: 2rem;
  text-align: center;
  color: #a0aec0;
  background: #2d3748;
  border-radius: 0.5rem;
  margin-top: 1rem;
}

.error-state {
  color: #fc8181;
  background: #742a2a;
}
</style>
